import { useRef } from "react";

import { HistoryEntityType, type FullAirHandlerUnit } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { ActionButton, Button, Dialog, Field } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { ArrowRightIcon, PointChartIcon } from "@eisox/icons";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useSelector } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";

import { HISTORY_LOOKBACK_DAYS } from "~/constants/history.constant";
import { exportData, getDateOptions } from "~/helpers/history.helper";
import { fetchBuildingHistory } from "~/hooks/use-building-history";
import { m } from "~/paraglide/messages";

import type { HistoryPointSelection } from "../helpers/data-selector.helper";
import { toAhuExportRows, transformAhuHistoryData } from "../helpers/history.helper";
import { AirHandlerUnitDataSelector } from "./air-handler-unit-data-selector";

interface ExportFormValues {
  data: HistoryPointSelection[];
  start: string;
  end: string;
}

interface DataExportDialogProps extends Dialog.RootProps {
  buildingId: string;
  airHandlerUnits: FullAirHandlerUnit[];
  data: HistoryPointSelection[];
  date: string;
}

export const DataExportDialog: React.FC<DataExportDialogProps> = ({
  buildingId,
  airHandlerUnits,
  data: dataProp,
  date: dateProp,
  open: openProp,
  // oxlint-disable-next-line typescript/unbound-method
  onOpenChange,
  defaultOpen = false,
  ...props
}) => {
  const formatRef = useRef<"csv" | "xlsx">("csv");
  const queryClient = useQueryClient();

  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen,
  });

  const form = useAppForm({
    defaultValues: {
      data: dataProp,
      start: dateProp,
      end: dayjs(dateProp).endOf("day").toISOString(),
    } satisfies ExportFormValues,
    onSubmit: async ({ value }) => {
      const historyData = await fetchBuildingHistory(
        queryClient,
        buildingId,
        value.start,
        value.end,
        HistoryEntityType.AIR_HANDLER_UNIT,
        value.data,
      );
      const rows = toAhuExportRows(transformAhuHistoryData(historyData, value.data), value.data, airHandlerUnits);
      exportData(rows, dayjs().format("YYYY-MM-DD_HHmm"), formatRef.current);
    },
  });

  const selected = useSelector(form.store, state => state.values.data);
  const startDate = useSelector(form.store, state => state.values.start);

  const startDateOptions = getDateOptions(true, HISTORY_LOOKBACK_DAYS);
  const endDateOptions = getDateOptions(false, HISTORY_LOOKBACK_DAYS).filter(o => dayjs(o.value).isAfter(startDate));

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      form.reset({
        data: dataProp,
        start: dateProp,
        end: dayjs(dateProp).endOf("day").toISOString(),
      });
    }
    setOpen(nextOpen);
  };

  const exportFormatLabels: Record<"csv" | "xlsx", () => string> = {
    csv: () => `${m.export()} (.csv)`,
    xlsx: () => `${m.export()} (.xlsx)`,
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange} {...props}>
      <Dialog.Trigger asChild>
        <Button className="ml-auto">
          {`${m.export()} (.csv, .xlsx)`} <ArrowRightIcon />
        </Button>
      </Dialog.Trigger>
      <Dialog.Content title={m.export_data()} icon={<PointChartIcon />} aria-describedby={undefined}>
        <form
          className="gap-5 flex w-full flex-col"
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <div className="gap-5 *:w-60 grid grid-cols-[1fr_1fr] grid-rows-[1fr_1fr] [&>:nth-child(1)]:col-span-2 [&>:nth-child(1)]:w-full">
            <form.AppForm>
              <form.AppField name="data">
                {field => (
                  <Field.Root className="*:box-border *:w-full">
                    <Field.Label>{m.data()}</Field.Label>
                    <AirHandlerUnitDataSelector
                      airHandlerUnits={airHandlerUnits}
                      selected={field.state.value}
                      onChange={field.handleChange}
                    />
                  </Field.Root>
                )}
              </form.AppField>
              <form.AppField name="start">
                {field => (
                  <field.Select
                    multiple={false}
                    searchInput={false}
                    label={m.begin()}
                    options={startDateOptions}
                    classNames={{ control: "w-full" }}
                  />
                )}
              </form.AppField>
              <form.AppField name="end">
                {field => (
                  <field.Select
                    multiple={false}
                    searchInput={false}
                    label={m.end()}
                    options={endDateOptions}
                    classNames={{ control: "w-full" }}
                  />
                )}
              </form.AppField>
            </form.AppForm>
          </div>
          <div className="gap-2.5 flex items-center justify-center">
            <Dialog.Close asChild>
              <ActionButton variant="cancel">{m.cancel()}</ActionButton>
            </Dialog.Close>
            {(["csv", "xlsx"] as const).map(format => (
              <ActionButton
                key={format}
                type="submit"
                disabled={selected.length === 0}
                onClick={() => {
                  formatRef.current = format;
                }}
              >
                {exportFormatLabels[format]()}
              </ActionButton>
            ))}
          </div>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export type { DataExportDialogProps };
